import React, { useState, useEffect } from "react";
import axios from "axios";
import { UserPlus, Trash2, Edit2, Save, X } from "lucide-react";

const UserManagement = ({ currentUser, onUserUpdate }) => {
    const [users, setUsers] = useState([]);
    const [formData, setFormData] = useState({ name: "", email: "", password: "", role: "User", location: "Mumbai", daily_target: "" });

    const [editingUserId, setEditingUserId] = useState(null);
    const [editFormData, setEditFormData] = useState({ name: "", email: "", role: "User", location: "Mumbai", daily_target: "" });

    // ✅ GET profile listings cleanly from the directory index endpoints
    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get("/api/users", {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data && Array.isArray(res.data)) {
                setUsers(res.data);
            }
        } catch (err) {
            console.error("Failed to fetch employees", err);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Handle Creating a New Employee Account
    const handleCreateAccount = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem("token");

            const res = await axios.post("/api/users/create", {
                name: formData.name,
                username: formData.email.trim(),
                password: formData.password,
                role: formData.role,
                location: formData.location,
                daily_target: formData.daily_target
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data?.success) {
                alert("Account created successfully!");
                setFormData({ name: "", email: "", password: "", role: "User", location: "Mumbai", daily_target: "" });
                fetchUsers(); // Refresh grid layout context

                // 🔥 Trigger a dynamic global reload for table dropdown selectors if present
                if (typeof onUserUpdate === "function") {
                    onUserUpdate();
                }
            }
        } catch (err) {
            alert(err.response?.data?.message || "Failed to create account. Check connection.");
        }
    };

    const handleStartEdit = (user) => {
        setEditingUserId(user.id);
        setEditFormData({
            name: user.name || "",
            email: user.email || "",
            role: user.role || "User",
            location: user.location || "Mumbai",
            daily_target: user.daily_target !== null && user.daily_target !== undefined ? user.daily_target : ""
        });
    };

    const handleCancelEdit = () => {
        setEditingUserId(null);
    };

    const handleEditInputChange = (e) => {
        setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
    };

    const handleUpdateUser = async (id) => {
        if (!editFormData.name.trim() || !editFormData.email.trim()) return;

        try {
            const token = localStorage.getItem("token");
            const res = await axios.put(`/api/users/update/${id}`, {
                name: editFormData.name.trim(),
                email: editFormData.email.trim(),
                role: editFormData.role,
                location: editFormData.location,
                daily_target: editFormData.daily_target
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data?.success) {
                alert("Employee details updated successfully!");
                setEditingUserId(null);
                fetchUsers();
                if (typeof onUserUpdate === "function") {
                    onUserUpdate();
                }
            }
        } catch (err) {
            alert(err.response?.data?.message || "Failed to update employee details.");
        }
    };

    // Handle Deleting an Employee Account
    const handleDeleteAccount = async (id) => {
        if (!id) return;
        if (!window.confirm("Are you sure you want to delete this employee account?")) return;

        try {
            const token = localStorage.getItem("token");
            await axios.delete(`/api/users/delete/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("Account deleted successfully!");
            fetchUsers();

            // 🔥 Trigger a dynamic global reload for table dropdown selectors if present
            if (typeof onUserUpdate === "function") {
                onUserUpdate();
            }
        } catch (err) {
            alert(err.response?.data?.message || "Failed to delete account.");
        }
    };

    // Handle Resetting an Employee's Password (Admin/Manager action)
    const handleResetPassword = async (id, newPassword) => {
        if (!id || !newPassword) return;
        try {
            const token = localStorage.getItem("token");
            const res = await axios.put("/api/users/reset-password", {
                userId: id,
                newPassword: newPassword
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data?.success) {
                alert("Password reset successfully!");
            }
        } catch (err) {
            alert(err.response?.data?.message || "Failed to reset password.");
        }
    };

    return (
        <div style={{ padding: "20px", backgroundColor: "white", borderRadius: "10px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
            <h3 style={{ marginBottom: "20px", color: "#1a2233", fontWeight: "bold" }}>
                👤 Employee Account Management
            </h3>

            {/* Account Creation Form */}
            <form onSubmit={handleCreateAccount} style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "30px", alignItems: "center" }}>
                <input type="text" name="name" placeholder="Full Name" value={formData.name} onChange={handleInputChange} required style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px" }} />
                <input type="text" name="email" placeholder="Username / Email" value={formData.email} onChange={handleInputChange} required style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px" }} />
                <input type="password" name="password" placeholder="Password" value={formData.password} onChange={handleInputChange} required style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px" }} />
                <select name="role" value={formData.role} onChange={handleInputChange} style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", backgroundColor: "white", fontSize: "13px", cursor: "pointer" }}>
                    <option value="User">User / Employee</option>
                    <option value="AccountManager">Account Manager</option>
                    <option value="Admin">Admin</option>
                </select>
                <select name="location" value={formData.location} onChange={handleInputChange} style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", backgroundColor: "white", fontSize: "13px", cursor: "pointer" }}>
                    <option value="Mumbai">Mumbai</option>
                    <option value="Sawantwadi">Sawantwadi</option>
                    <option value="Solapur">Solapur</option>
                </select>
                <input type="number" name="daily_target" placeholder="Daily Target (e.g. 12)" value={formData.daily_target} onChange={handleInputChange} style={{ padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "13px", width: "150px" }} />
                <button type="submit" style={{ padding: "8px 16px", backgroundColor: "#00a3e0", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: "600" }}>
                    <UserPlus size={16} /> Create Account
                </button>
            </form>

            {/* Account List Table */}
            <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                        <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #edf2f7", textAlign: "left", color: "#4a5568" }}>
                            <th style={{ padding: "12px 16px" }}>Name</th>
                            <th style={{ padding: "12px 16px" }}>Username / Email</th>
                            <th style={{ padding: "12px 16px" }}>Role</th>
                            <th style={{ padding: "12px 16px" }}>Location</th>
                            <th style={{ padding: "12px 16px" }}>Daily Target</th>
                            <th style={{ padding: "12px 16px", textAlign: "center" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 ? (
                            <tr>
                                <td colSpan="6" style={{ padding: "20px", textAlign: "center", color: "#a0aec0", fontStyle: "italic" }}>
                                    No employee accounts loaded. Use the form above to add your first user.
                                </td>
                            </tr>
                        ) : (
                            users.map((user, idx) => {
                                const targetId = user.id || idx;
                                const empName = user.name || "Unknown Employee";
                                const empUsername = user.email || "N/A";
                                const empRole = String(user.role || "User").trim();
                                const isEditing = user.id === editingUserId;

                                return (
                                    <tr key={targetId} style={{ borderBottom: "1px solid #edf2f7", backgroundColor: idx % 2 === 0 ? "transparent" : "#f8fafc" }}>
                                        {isEditing ? (
                                            <>
                                                <td style={{ padding: "8px 12px" }}>
                                                    <input type="text" name="name" value={editFormData.name} onChange={handleEditInputChange} style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #cbd5e1", width: "100%", fontSize: "13px" }} />
                                                </td>
                                                <td style={{ padding: "8px 12px" }}>
                                                    <input type="text" name="email" value={editFormData.email} onChange={handleEditInputChange} style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #cbd5e1", width: "100%", fontSize: "13px" }} />
                                                </td>
                                                <td style={{ padding: "8px 12px" }}>
                                                    <select name="role" value={editFormData.role} onChange={handleEditInputChange} style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #cbd5e1", width: "100%", fontSize: "13px", cursor: "pointer" }}>
                                                        <option value="User">User</option>
                                                        <option value="AccountManager">AccountManager</option>
                                                        <option value="Admin">Admin</option>
                                                    </select>
                                                </td>
                                                <td style={{ padding: "8px 12px" }}>
                                                    <select name="location" value={editFormData.location} onChange={handleEditInputChange} style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #cbd5e1", width: "100%", fontSize: "13px", cursor: "pointer" }}>
                                                        <option value="Mumbai">Mumbai</option>
                                                        <option value="Sawantwadi">Sawantwadi</option>
                                                        <option value="Solapur">Solapur</option>
                                                    </select>
                                                </td>
                                                <td style={{ padding: "8px 12px" }}>
                                                    <input type="number" name="daily_target" value={editFormData.daily_target} onChange={handleEditInputChange} placeholder="12" style={{ padding: "6px 10px", borderRadius: "4px", border: "1px solid #cbd5e1", width: "100%", fontSize: "13px" }} />
                                                </td>
                                                <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                                    <div style={{ display: "flex", gap: "8px", justifyContent: "center", alignItems: "center" }}>
                                                        <button onClick={() => handleUpdateUser(user.id)} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", display: "inline-flex" }} title="Save">
                                                            <Save size={16} color="#10b981" />
                                                        </button>
                                                        <button onClick={handleCancelEdit} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", display: "inline-flex" }} title="Cancel">
                                                            <X size={16} color="#64748b" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td style={{ padding: "12px 16px", fontWeight: "600", color: "#2d3748" }}>{empName}</td>
                                                <td style={{ padding: "12px 16px", color: "#4a5568" }}>{empUsername}</td>
                                                <td style={{ padding: "12px 16px" }}>
                                                    <span style={{
                                                        padding: "3px 8px",
                                                        borderRadius: "4px",
                                                        fontSize: "11px",
                                                        fontWeight: "bold",
                                                        backgroundColor: empRole.toLowerCase() === "manager" || empRole.toLowerCase() === "admin" ? "#ebf8ff" : "#edf2f7",
                                                        color: empRole.toLowerCase() === "manager" || empRole.toLowerCase() === "admin" ? "#2b6cb0" : "#4a5568"
                                                    }}>
                                                        {empRole}
                                                    </span>
                                                </td>
                                                <td style={{ padding: "12px 16px", color: "#4a5568", fontWeight: "500" }}>{user.location || "-"}</td>
                                                 <td style={{ padding: "12px 16px", color: "#0f172a", fontWeight: "600" }}>
                                                     {(user.daily_target !== null && user.daily_target !== undefined && user.daily_target !== "") ? `${user.daily_target} files` : "12 files (Default)"}
                                                 </td>
                                                <td style={{ padding: "12px 16px", textAlign: "center" }}>
                                                    <div style={{ display: "flex", gap: "12px", justifyContent: "center", alignItems: "center" }}>
                                                        <button
                                                            onClick={() => handleStartEdit(user)}
                                                            style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", display: "inline-flex", alignItems: "center" }}
                                                            title="Edit Details"
                                                        >
                                                            <Edit2 size={15} color="#3182ce" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                const newPass = prompt(`Enter new password for ${empName}:`);
                                                                if (newPass && newPass.trim()) {
                                                                    handleResetPassword(targetId, newPass.trim());
                                                                }
                                                            }}
                                                            style={{
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                cursor: "pointer",
                                                                fontSize: "11px",
                                                                fontWeight: "bold",
                                                                color: "#3182ce",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: "3px"
                                                            }}
                                                            title="Reset Password"
                                                        >
                                                            🔑 Reset
                                                        </button>
                                                        <Trash2
                                                            size={16}
                                                            color="#e53e3e"
                                                            cursor="pointer"
                                                            onClick={() => handleDeleteAccount(targetId)}
                                                            style={{ display: "inline-block" }}
                                                            title="Delete Account"
                                                        />
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UserManagement;
